import { Component, OnInit } from '@angular/core';
import { AddTableComponent } from '../../components/modals/add-table/add-table.component';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { TableService } from '../../services/table/table.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService } from '../../services/auth/auth.service';
import { NotifierService } from 'angular-notifier';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogComponent } from '../../components/modals/confirm-dialog/confirmation-dialog.component';
import { IRestaurantInfo } from '../../interfaces/restaurant';
import { CallWaiteConfigComponent } from '../../components/modals/call-waite-config/call-waite-config.component';
import { of } from 'rxjs';
import { AdminService } from '../../services/admin/admin.service';
import { animate, keyframes, style, transition, trigger } from '@angular/animations';

@UntilDestroy()
@Component({
  selector: 'app-tables-page',
  templateUrl: './tables-page.component.html',
  styleUrls: ['./tables-page.component.scss'],
  animations: [
    trigger('smoothGrow', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden', paddingTop: 0, paddingBottom: 0 }),
        animate('300ms ease-out', style({ height: '*', opacity: 1, paddingTop: '*', paddingBottom: '*' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden', paddingTop: '*', paddingBottom: '*' }),
        animate('200ms ease-in', keyframes([
          style({ opacity: 0.5, offset: 0.4 }),
          style({ height: 0, opacity: 0, paddingTop: 0, paddingBottom: 0, offset: 1 })
        ]))
      ]),
    ]),
  ]
})
export class TablesPageComponent implements OnInit {

  public isEdited = false;
  public editable = false;
  public isWaiter: boolean | undefined;
  public restaurant!: IRestaurantInfo;
  public language = localStorage.getItem('lang') ?? this.translate.currentLang;
  public selectedTables = new Set<number>();
  public isSelectAllTables = false;

  constructor(
    public router: Router,
    private adminService: AdminService,
    public tables: TableService,
    public auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private matDialog: MatDialog,
    private notifier: NotifierService,
    private translate: TranslateService,
    public  tableService: TableService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.parent?.paramMap.pipe(
      map(params => params.get('id')),
      switchMap(id => id ? this.adminService.getRestaurantInfo(+id) : of(null)),
      filter((restaurant): restaurant is IRestaurantInfo => restaurant !== null && restaurant !== undefined),
      tap(restaurant => {
        this.restaurant = restaurant;
        this.editable = this.auth.isAdmin(restaurant);
        this.isWaiter = this.auth.isWaiter(restaurant);
        this.isEdited = false;
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  addTable(): void {
    const addTable = this.matDialog.open(AddTableComponent, {
      data: {
        restaurantId: this.restaurant.id
      },
    });

    addTable.afterClosed().pipe(
      filter(Boolean),
    ).subscribe(() => {
      this.notifier.notify('success', this.translate.instant('pages.tables.messages.addTable'));
    });
  }

  turnOffTables(): void {
    const title = this.translate.instant('pages.tables.modal.turnOff');
    const message = this.translate.instant('pages.tables.modal.turnOffDevices');
    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });
    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe(() => {
      this.tables.items.filter(table => this.selectedTables.has(table.id)).forEach(table => {
        this.tables.requestDeviceTurnOff(table);
      });
    });
  }

  changeSelectedTables({event, id}: {event: boolean, id: number}): void {
    switch (event) {
      case true:
        this.selectedTables.add(id);
        break;
      case false:
        this.selectedTables.delete(id);
        break;
    }
  }

  public clearSelectedTables(): void {
    this.selectedTables.clear();
  }

  public isEditedToggle(): void {
    if (this.isEdited) { this.clearSelectedTables(); }
    this.isEdited = !this.isEdited;
    this.isSelectAllTables = false;
  }

  public selectAllTablesToggle(): void {
    if (this.isSelectAllTables) {
      this.selectedTables.clear();
      this.isSelectAllTables = false;
    } else {
      const onlineTables = this.tables.items.filter(table => table.online === true);
      const onlineTablesIds = onlineTables.map(table => table.id);
      onlineTablesIds.forEach(id => this.selectedTables.add(id));
      this.isSelectAllTables = true;
    }
  }

  callWaiterConfig(): void {
    const callWaiterConfig = this.matDialog.open(CallWaiteConfigComponent, {
      data: {
        restaurantId: this.restaurant.id,
      },
    });

    callWaiterConfig.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((data) => {
      const info = data as IRestaurantInfo;

      this.restaurant = info ? info : this.restaurant;
      this.notifier.notify('success', this.translate.instant('pages.tables.messages.callWaiterConfig'));
    });
  }
}
